import Skeleton from '@material-ui/lab/Skeleton';
import { cloneDeep, Dictionary, isEmpty, isEqual } from 'lodash';
import { groupBy, sortBy } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IReport } from '~/src/dash/api/console-api';
import {
  CollapsibleCheckboxes,
  EDisplayExpandOrCollapseAll,
  handleClickCollapseAll,
  handleClickExpandAll,
} from '~/src/dash/components/CollapsibleCheckboxes';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacingV2 } from '~/src/dash/components/layout/common';
import { getLineHeight } from '~/src/dash/components/text/common';
import { TextWithTooltip } from '~/src/dash/components/text/TextWithTooltip';
import {
  borderRadius,
  Col,
  Cols,
  Row,
  Rows,
  Text,
  TextButton,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  IOrderBy,
  REPORTING_MAX_SELECTED_CHARS,
} from '~/src/dash/views/reporting/ReportBuilder/common';

interface IMetricsSectionProps {
  metrics: IMetric[];
  selectedMetricKeys: string[];
  setSelectedMetricKeys: (ids: string[]) => void;
  orderBys: IOrderBy[];
  setOrderBys: (orderBys: IOrderBy[]) => void;
  isLoading: boolean;
  report: IReport | null;
  disabledMetricKeys: string[];
}

interface IMetricSection {
  label: string;
  metrics: IMetric[];
}

interface IMetric {
  title: string;
  ID: string;
  disabled: boolean;
  subheader: string;
  selected: boolean;
  hidden?: boolean;
}

export const MetricsSection = ({
  metrics,
  selectedMetricKeys,
  setSelectedMetricKeys,
  orderBys,
  setOrderBys,
  isLoading,
  report,
  disabledMetricKeys,
}: IMetricsSectionProps) => {
  const { t } = usePublicaTranslation();

  const [displayExpandOrCollapseAll, setDisplayExpandOrCollapseAll] = useState(
    EDisplayExpandOrCollapseAll.Collapse,
  );
  const [isExpandAll, setIsExpandAll] = useState<boolean>(true);
  const [isCollapseAll, setIsCollapseAll] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [disabledKeys, setDisabledKeys] = useState<string[]>(disabledMetricKeys);

  useEffect(() => {
    if (!isEqual(sortBy(disabledMetricKeys), sortBy(disabledKeys))) {
      setDisabledKeys(disabledMetricKeys);
    }
  }, [disabledMetricKeys]);

  useEffect(() => {
    if (!isEmpty(disabledKeys) && !isEmpty(selectedMetricKeys)) {
      const newSelectedMetricKeys: string[] = [];
      selectedMetricKeys.forEach((metricKey) => {
        if (!disabledKeys.includes(metricKey)) {
          newSelectedMetricKeys.push(metricKey);
        }
      });
      setSelectedMetricKeys(newSelectedMetricKeys);
    }
  }, [disabledKeys]);

  useEffect(() => {
    if (search.length > 0) {
      setIsExpandAll(true);
    }
  }, [search]);

  useEffect(() => {
    unstable_batchedUpdates(() => {
      if (report) {
        setDisplayExpandOrCollapseAll(EDisplayExpandOrCollapseAll.Expand);
        setIsExpandAll(false);
        setIsCollapseAll(true);
      }
    });
  }, [report]);

  const onExpandAll = () => {
    unstable_batchedUpdates(() => {
      handleClickExpandAll(setIsExpandAll, setIsCollapseAll);
      setDisplayExpandOrCollapseAll(EDisplayExpandOrCollapseAll.Collapse);
    });
  };

  const onCollapseAll = () => {
    unstable_batchedUpdates(() => {
      handleClickCollapseAll(setIsExpandAll, setIsCollapseAll);
      setDisplayExpandOrCollapseAll(EDisplayExpandOrCollapseAll.Expand);
    });
  };

  const removeExtraOrderBys = (orderBys: IOrderBy[], selectedMetrics: string[]): IOrderBy[] => {
    const orderBysCopy = cloneDeep(orderBys).filter(
      (orderBy) => selectedMetrics.includes(orderBy.key) || orderBy.key === '',
    );
    // if there are more orderBys than selected selectedMetrics after filtering
    // out non selected selectedMetrics, remove orderBys w/ no selectedMetrics selected
    for (let i = 0; i < orderBysCopy.length - selectedMetrics.length; i++) {
      orderBysCopy.splice(
        orderBysCopy.findIndex((orderBy) => orderBy.key === ''),
        1,
      );
    }
    return orderBysCopy;
  };

  const handleClickCheckbox = (id: string): void => {
    // do nothing if the metric is disabled
    if (metrics.find((metric) => metric.ID === id)?.disabled) return;

    // otherwise select or deselect the metric accordingly
    const newSelectedMetricKeys = [...selectedMetricKeys];
    if (newSelectedMetricKeys.includes(id)) {
      newSelectedMetricKeys.splice(newSelectedMetricKeys.indexOf(id), 1);
    } else {
      newSelectedMetricKeys.push(id);
    }

    unstable_batchedUpdates(() => {
      setOrderBys(removeExtraOrderBys(orderBys, newSelectedMetricKeys));
      setSelectedMetricKeys(newSelectedMetricKeys);
    });
  };

  const handleClickCheckboxColumn = (ids: string[], selected: boolean) => {
    const newSelectedMetricKeys = cloneDeep(selectedMetricKeys);
    ids.forEach((id) => {
      if (selected) {
        if (!newSelectedMetricKeys.includes(id) && !disabledMetricKeys.includes(id)) {
          newSelectedMetricKeys.push(id);
        }
      } else {
        newSelectedMetricKeys.splice(newSelectedMetricKeys.indexOf(id), 1);
      }
    });
    unstable_batchedUpdates(() => {
      setOrderBys(removeExtraOrderBys(orderBys, newSelectedMetricKeys));
      setSelectedMetricKeys(newSelectedMetricKeys);
    });
  };

  const handleClearMetrics = () => {
    setSelectedMetricKeys([]);
  };

  const sortMetricSections = (metricSections: Dictionary<IMetric[]>): IMetricSection[] => {
    const sortedMetricSections = [] as IMetricSection[];

    const METRIC_SECTIONS_IN_ORDER = [
      'Most Used',
      'Revenue',
      'Ratios',
      'Pre-Enrichment Metadata Rate',
      'Latency',
      'Analysis',
      'Others',
      'VPAID',
      'Auctions',
      'Pod Duration',
      'Line Item Metrics',
      'Client-Side Tracking',
      'Average per Session',
      'Errors',
      'Enrichment Rate',
      'Inventory Routes',
      'Sum with adomains',
      'Sum with different adomains',
      'Total number of detected languages by EleaAI',
      'Number of unique detected languages by EleaAI',
      'Total number of adomains by EleaAI',
      'Total number of mediafiles by EleaAI',
      'Total number of IAB Categories by EleaAI',
      'Number of unique adomains by EleaAI',
      'Number of unique mediafiles by EleaAI',
      'Summary',
      'Ad Refresh',
      'Exposure',
      'GroupM Viewability',
      'Publicis Viewability',
      'Out of View Ads',
      'Invalid Traffic',
      'Invalid Traffic By Type',
      'Failure Reasons',
      'Failure Risk Categories',
      'Blocked Reasons',
      'Blocked Risk Categories',
      'Video',
      // Smart Floor Sections
      'Bid Requests',
      'Bid Responses',
      'Avg Winning Bids',
      'Bid Floors',
      'Bid Wons',
      'Uplift',
      'Ad Impressions',
    ];

    METRIC_SECTIONS_IN_ORDER.forEach((metricSectionLabel) => {
      const metricSection = metricSections[metricSectionLabel];
      if (metricSections[metricSectionLabel]) {
        sortedMetricSections.push({
          label: metricSectionLabel,
          metrics: sortBy(metricSection, 'title', 'asc'),
        });
      }
    });

    return sortedMetricSections;
  };
  const sortMetricSectionsIntoCols = (
    metricSections: Dictionary<IMetric[]>,
  ): IMetricSection[][] => {
    const sortedMetricSections = sortMetricSections(metricSections);
    const cols = [[], [], [], []] as IMetricSection[][];
    sortedMetricSections.forEach((section, idx) => cols[idx % 4].push(section));
    return cols;
  };

  const createMetricSections = (metrics: IMetric[]): IMetricSection[][] => {
    const metricsGrouped = groupBy(metrics, 'subheader');
    return sortMetricSectionsIntoCols(metricsGrouped);
  };

  const getFilteredMetrics = (): IMetric[] => {
    const query = search.toLowerCase();
    return search.length > 0
      ? metrics.filter((metric) => {
          const title = metric.title.toLowerCase();
          const subheader = metric.subheader.toLowerCase();
          return title.includes(query) || subheader.includes(query);
        })
      : metrics;
  };

  const filteredMetrics = getFilteredMetrics();
  const metricSections = createMetricSections(filteredMetrics);
  const selectedMetricNames = metrics
    .filter((metric) => metric.selected)
    .map((metric) => metric.title)
    .join(', ');

  return (
    <Rows>
      <Row>
        <Cols gap={spacingV2.medium}>
          <Col displayFlex style={{ alignItems: 'center' }}>
            <IconV2 name='activity' />
          </Col>
          <Col displayFlex extraSmallVerticalPaddingV2 style={{ alignItems: 'center' }}>
            <Text tKey='Metrics' sizeV2 bold large />
          </Col>
          {selectedMetricKeys.length > 0 && (
            <>
              <Col
                displayFlex
                style={{
                  alignItems: 'center',
                  paddingLeft: spacingV2.extraLarge,
                }}
              >
                <Text tKey='NumSelected' tProps={{ count: selectedMetricKeys.length }} small bold />
              </Col>
              <Col displayFlex style={{ alignItems: 'center' }}>
                <TextWithTooltip
                  small
                  tooltip={`${selectedMetricNames}`}
                  style={{
                    borderRadius: borderRadius.base,
                    backgroundColor: publicaColors.veryLightBlue,
                    padding: `${spacingV2.extraSmall}`,
                  }}
                >
                  {selectedMetricNames.length < REPORTING_MAX_SELECTED_CHARS
                    ? `${selectedMetricNames}`
                    : `${selectedMetricNames.slice(0, REPORTING_MAX_SELECTED_CHARS)}...`}
                </TextWithTooltip>
              </Col>
              <Col displayFlex style={{ alignItems: 'center' }}>
                <TextButton size='small' weight='none' onClick={handleClearMetrics}>
                  <Text small tKey='Clear' style={{ color: 'inherit', fontWeight: 'inherit' }} />
                </TextButton>
              </Col>
            </>
          )}
        </Cols>
      </Row>

      <Rows extraLargeHorizontalPaddingV2>
        <Row extraSmallVerticalPaddingV2>
          <Cols gap={spacingV2.large} style={{ display: 'flex', alignItems: 'center' }}>
            <Col>
              <TextInput
                enableClear
                handleOnChange={(input: string) => setSearch(input)}
                inputSize='small'
                msDelay={500}
                placeholder={t('Search')}
                prefix='search'
              />
            </Col>
            {displayExpandOrCollapseAll === EDisplayExpandOrCollapseAll.Expand && (
              <Col>
                <TextButton
                  weight='none'
                  size='small'
                  onClick={onExpandAll}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <IconV2 name='expand' color={publicaColors.blue} />
                  <Text
                    small
                    tKey='ExpandAll'
                    style={{ color: 'inherit', fontWeight: 'inherit' }}
                  />
                </TextButton>
              </Col>
            )}
            {displayExpandOrCollapseAll === EDisplayExpandOrCollapseAll.Collapse && (
              <Col displayFlex style={{ alignItems: 'center' }}>
                <TextButton
                  weight='none'
                  size='small'
                  onClick={onCollapseAll}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <IconV2 name='collapse' color={publicaColors.blue} />
                  <Text
                    small
                    tKey='CollapseAll'
                    style={{ color: 'inherit', fontWeight: 'inherit' }}
                  />
                </TextButton>
              </Col>
            )}
          </Cols>
        </Row>
        <Row>
          <Cols wrap>
            {metricSections.map((section) => {
              return !isLoading ? (
                <Col
                  style={{
                    width: section.length > 0 ? `calc(100% / ${metricSections.length})` : 0,
                    minWidth: section.length > 0 ? '10rem' : 0,
                  }}
                >
                  {section.map((metricSection) => {
                    return (
                      <CollapsibleCheckboxes
                        title={metricSection.label}
                        list={metricSection.metrics}
                        onChange={handleClickCheckbox}
                        onSectionChange={handleClickCheckboxColumn}
                        isExpandAll={isExpandAll}
                        setIsExpandAll={setIsExpandAll}
                        isCollapseAll={isCollapseAll}
                        setIsCollapseAll={setIsCollapseAll}
                      />
                    );
                  })}
                </Col>
              ) : (
                <Skeleton
                  variant='text'
                  animation='wave'
                  width={`calc(100% / ${metricSections.length} - ${spacingV2.large})`}
                  height={getLineHeight('extraLarge')}
                  style={{ marginRight: spacingV2.large }}
                />
              );
            })}
          </Cols>
        </Row>
      </Rows>
    </Rows>
  );
};
